import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import ThemeToggle from '../common/ThemeToggle';
import CustomerChat from './CustomerChat';
import CustomerProfile from './CustomerProfile';
import CustomerTicketHistory from './CustomerTicketHistory';
import apiService from '../../services/api';
import { useCustomerTranslation } from '../../contexts/CustomerTranslationContext';

interface CustomerDashboardProps {}

const CustomerDashboard: React.FC<CustomerDashboardProps> = () => {
  const { user, isAuthenticated, isLoading, logout } = useAuth();
  const { t } = useCustomerTranslation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'chat' | 'profile' | 'tickets'>('chat');
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [customerData, setCustomerData] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  const loadCustomerData = useCallback(async () => {
    if (!user?.id && !user?.email) return;
    
    try {
      setLoading(true);
      // Use email as identifier for better company association lookup
      const identifier = user.email || user.id;
      console.log('🔍 Loading customer data for identifier:', identifier, '(user id:', user.id, ')');
      const data = await apiService.getCustomer(identifier);
      console.log('📊 Received customer data:', data);
      console.log('🏢 Associated companies in data:', data?.associatedCompanies);
      console.log('🏢 Customer company from user record:', data?.company);
      console.log('🏢 Customer company_id from user record:', data?.company_id);
      console.log('🔍 Full data structure:', JSON.stringify(data, null, 2));
      setCustomerData(data);
    } catch (error) {
      console.error('Error loading customer data:', error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Load customer data if authenticated
  useEffect(() => {
    console.log('🔄 CustomerDashboard useEffect triggered with:', {
      isAuthenticated,
      userType: user?.userType,
      userEmail: user?.email,
      userId: user?.id,
      hasLoadFunction: typeof loadCustomerData === 'function'
    });
    
    if (isAuthenticated && user?.userType === 'customer') {
      console.log('🔄 CustomerDashboard: Triggering loadCustomerData for user:', user?.email, user?.id);
      loadCustomerData();
    } else {
      console.log('🔄 CustomerDashboard: Not loading data - authenticated:', isAuthenticated, 'userType:', user?.userType);
    }
  }, [isAuthenticated, user]);

  const handleLogin = () => {
    navigate('/customer/login');
  };

  const handleLogout = async () => {
    await logout();
    setActiveTab('chat');
    setCustomerData(null);
  };

  const handleProfileUpdate = async (updatedData: any) => {
    if (!user?.id) return;
    
    try {
      // Update customer profile via the profile API
      await apiService.updateCustomerProfile(updatedData);
      
      // Reload customer data to get fresh information
      await loadCustomerData();
    } catch (error) {
      console.error('Error updating customer profile:', error);
      throw error;
    }
  };


  const renderTabNavigation = () => {
    if (!isAuthenticated || user?.userType !== 'customer') {
      return null;
    }

    return (
      <div className="border-b border-gray-200 dark:border-gray-700 mb-4">
        <nav className="-mb-px flex space-x-6">
          <button
            onClick={() => setActiveTab('chat')}
            className={`py-2.5 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'chat'
                ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
          >
            <span className="flex items-center">
              <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
              {t.supportChat}
            </span>
          </button>
          <button
            onClick={() => setActiveTab('tickets')}
            className={`py-2.5 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'tickets'
                ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
          >
            <span className="flex items-center">
              <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
              {t.history}
            </span>
          </button>
          <button
            onClick={() => setActiveTab('profile')}
            className={`py-2.5 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'profile'
                ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
          >
            <span className="flex items-center">
              <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              {t.profile}
            </span>
          </button>
        </nav>
      </div>
    );
  };

  // Debug logging for authentication state
  console.log('CustomerDashboard render:', { 
    isLoading, 
    isAuthenticated, 
    userType: user?.userType,
    userId: user?.id,
    userEmail: user?.email
  });
  
  console.log('🔍 CustomerDashboard state check:', {
    customerData: customerData ? 'has data' : 'null',
    loading,
    activeTab
  });

  // Show loading spinner while authentication is being checked
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="flex flex-col items-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading your account...</p>
        </div>
      </div>
    );
  }

  const renderContent = () => {
    // If not authenticated or not a customer, show customer chat (which includes login option)
    if (!isAuthenticated || user?.userType !== 'customer') {
      console.log('CustomerDashboard: Showing CustomerChat for non-authenticated or non-customer user');
      return <CustomerChat />;
    }

    switch (activeTab) {
      case 'profile':
        return (
          <CustomerProfile 
            customerData={customerData}
            onUpdate={handleProfileUpdate}
            loading={loading}
          />
        );
      case 'tickets':
        return (
          <CustomerTicketHistory 
            customerId={user.id}
            customerData={customerData}
          />
        );
      case 'chat':
      default:
        return <CustomerChat />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-4">
              <img 
                src="/neurovirtual-logo.png" 
                alt="NeuroVirtual Logo" 
                className="h-8 w-8 object-contain"
              />
              <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                NeuroVirtual Support
              </h1>
            </div>
            <ThemeToggle />
          </div>
        </div>
      </div>

      {/* Main Content with Sidebar Layout */}
      <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Main Content Area */}
          <div className="flex-1 min-w-0">
            {/* Welcome Banner - Only for authenticated users at top */}
            {isAuthenticated && user?.userType === 'customer' && (
              <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3 mb-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <div className="w-7 h-7 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                    </div>
                    <div>
                      <p className="font-medium text-green-900 dark:text-green-100 text-sm">
                        {t.welcomeBack}, {user?.firstName} {user?.lastName}!
                      </p>
                      <p className="text-xs text-green-700 dark:text-green-300">
                        {user?.email}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={handleLogout}
                    className="text-xs text-green-700 dark:text-green-300 hover:text-green-900 dark:hover:text-green-100 underline whitespace-nowrap"
                  >
                    {t.logout}
                  </button>
                </div>
              </div>
            )}

            {/* Tab Navigation */}
            {renderTabNavigation()}

            {/* Content */}
            {renderContent()}
          </div>

          {/* Right Sidebar - Compact */}
          <div className="lg:w-80 space-y-4">
            {/* Login/History Banner */}
            {!isAuthenticated || user?.userType !== 'customer' ? (
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 shadow-sm">
                <div className="flex flex-col space-y-3">
                  <div className="flex items-start space-x-2">
                    <svg className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                    <div>
                      <h3 className="text-sm font-semibold text-blue-900 dark:text-blue-100">
                        {t.accessYourHistory}
                      </h3>
                      <p className="text-xs text-blue-700 dark:text-blue-300 mt-1">
                        {t.accessYourHistoryDesc}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={handleLogin}
                    className="w-full px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm font-medium transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900"
                  >
                    {t.login}
                  </button>
                </div>
              </div>
            ) : (
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 shadow-sm">
                <div className="flex items-start space-x-2">
                  <svg className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div>
                    <h3 className="text-sm font-semibold text-blue-900 dark:text-blue-100">
                      {t.history}
                    </h3>
                    <p className="text-xs text-blue-700 dark:text-blue-300 mt-1">
                      {t.viewTicketHistoryDesc}
                    </p>
                    <button
                      onClick={() => setActiveTab('tickets')}
                      className="mt-2 text-xs text-blue-700 dark:text-blue-300 hover:text-blue-900 dark:hover:text-blue-100 font-medium underline"
                    >
                      {t.viewTicketHistoryLink}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Survey Banner - Compact */}
            <div className="bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg p-4 text-white shadow-sm">
              <div className="flex flex-col space-y-3">
                <div className="flex items-start space-x-2">
                  <div className="w-8 h-8 bg-white bg-opacity-20 rounded-full flex items-center justify-center flex-shrink-0">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-semibold text-white text-sm">
                      {t.helpUsImprove}
                    </h3>
                    <p className="text-blue-100 text-xs mt-1">
                      {t.helpUsImproveDesc}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => window.open('https://survey.alchemer.com/s3/8140941/Customer-Service-Satisfaction-2025B', '_blank')}
                  className="w-full px-3 py-2 bg-white text-blue-600 rounded-md text-sm font-medium hover:bg-blue-50 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-blue-500"
                >
                  {t.takeSurvey}
                </button>
              </div>
            </div>

            {/* Quick Help Tips */}
            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 shadow-sm">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2 flex items-center">
                <svg className="w-4 h-4 mr-1 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {t.quickTips}
              </h3>
              <ul className="text-xs text-gray-600 dark:text-gray-400 space-y-2">
                <li className="flex items-start">
                  <span className="text-blue-600 dark:text-blue-400 mr-1.5">•</span>
                  <span>{t.quickTip1}</span>
                </li>
                <li className="flex items-start">
                  <span className="text-blue-600 dark:text-blue-400 mr-1.5">•</span>
                  <span>{t.quickTip2}</span>
                </li>
                <li className="flex items-start">
                  <span className="text-blue-600 dark:text-blue-400 mr-1.5">•</span>
                  <span>{t.quickTip3}</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerDashboard;
